import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    createPaymentUrl: String,
    publishableKey: String,
    returnUrl: String,
    monthlyPrice: Number,
    yearlyPrice: Number,
    connectionCount: Number
  }

  static targets = [
    "monthlyBtn", "yearlyBtn", "price", "period",
    "paymentElement", "loadingState", "submitButton", "buttonText", "spinner", "errors"
  ]

  async connect() {
    this.interval = 'monthly'
    this.stripe = null
    this.elements = null
    this.paymentElement = null

    // Load Stripe.js
    if (typeof Stripe === 'undefined') {
      await this.loadStripeJs()
    }

    this.stripe = Stripe(this.publishableKeyValue)

    // Create initial payment intent
    await this.createPaymentIntent()
  }

  loadStripeJs() {
    return new Promise((resolve) => {
      const script = document.createElement('script')
      script.src = 'https://js.stripe.com/v3/'
      script.onload = resolve
      document.head.appendChild(script)
    })
  }

  async createPaymentIntent() {
    this.showLoading()
    this.hideError()

    try {
      const response = await fetch(this.createPaymentUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ interval: this.interval })
      })

      const data = await response.json()

      if (data.error) {
        this.showError(data.error)
        return
      }

      this.clientSecret = data.client_secret
      this.setupPaymentElement()
    } catch (error) {
      this.showError('Failed to initialize payment. Please refresh and try again.')
    }
  }

  setupPaymentElement() {
    // Create Elements with dark theme matching UI
    const appearance = {
      theme: 'night',
      variables: {
        colorPrimary: '#a855f7',
        colorBackground: '#2d2d44',
        colorText: '#ffffff',
        colorDanger: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px',
        spacingUnit: '4px',
        fontSizeBase: '14px'
      },
      rules: {
        '.Input': {
          backgroundColor: '#1e1e2f',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          boxShadow: 'none'
        },
        '.Input:focus': {
          border: '1px solid #a855f7',
          boxShadow: '0 0 0 1px rgba(168, 85, 247, 0.3)'
        },
        '.Label': {
          color: '#a1a1aa'
        }
      }
    }

    this.elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      appearance
    })

    // Unmount existing element if present
    if (this.paymentElement) {
      this.paymentElement.unmount()
    }

    this.paymentElement = this.elements.create('payment', {
      layout: 'tabs'
    })

    this.paymentElement.mount('#payment-element')
    this.hideLoading()
  }

  async selectMonthly() {
    if (this.interval === 'monthly') return
    this.interval = 'monthly'
    this.updateDisplay()
    await this.createPaymentIntent()
  }

  async selectYearly() {
    if (this.interval === 'yearly') return
    this.interval = 'yearly'
    this.updateDisplay()
    await this.createPaymentIntent()
  }

  updateDisplay() {
    const isYearly = this.interval === 'yearly'
    const monthlyPrice = this.monthlyPriceValue.toFixed(2)
    const yearlyPrice = this.yearlyPriceValue.toFixed(2)

    // Update button styles
    if (this.hasMonthlyBtnTarget && this.hasYearlyBtnTarget) {
      if (isYearly) {
        this.monthlyBtnTarget.classList.remove('bg-purple-500', 'text-white')
        this.monthlyBtnTarget.classList.add('text-zinc-400', 'hover:text-white')
        this.yearlyBtnTarget.classList.remove('text-zinc-400', 'hover:text-white')
        this.yearlyBtnTarget.classList.add('bg-purple-500', 'text-white')
      } else {
        this.monthlyBtnTarget.classList.add('bg-purple-500', 'text-white')
        this.monthlyBtnTarget.classList.remove('text-zinc-400', 'hover:text-white')
        this.yearlyBtnTarget.classList.add('text-zinc-400', 'hover:text-white')
        this.yearlyBtnTarget.classList.remove('bg-purple-500', 'text-white')
      }
    }

    // Update price display
    if (this.hasPriceTarget) {
      this.priceTarget.textContent = isYearly ? `$${yearlyPrice}` : `$${monthlyPrice}`
    }

    if (this.hasPeriodTarget) {
      this.periodTarget.textContent = isYearly ? '/yr' : '/mo'
    }

    // Update button text
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = isYearly
        ? `Subscribe - $${yearlyPrice}/year`
        : `Subscribe - $${monthlyPrice}/month`
    }
  }

  async submit() {
    this.setLoading(true)
    this.hideError()

    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrlValue
        }
      })

      if (error) {
        this.showError(error.message)
        this.setLoading(false)
      }
      // If no error, the page will redirect to return_url
    } catch (e) {
      this.showError('An unexpected error occurred. Please try again.')
      this.setLoading(false)
    }
  }

  showLoading() {
    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.classList.remove('hidden')
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.add('hidden')
    }
    if (this.hasPaymentElementTarget) {
      this.paymentElementTarget.innerHTML = ''
    }
  }

  hideLoading() {
    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.classList.add('hidden')
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.remove('hidden')
    }
  }

  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
    }
    if (this.hasButtonTextTarget) {
      const isYearly = this.interval === 'yearly'
      const monthlyPrice = this.monthlyPriceValue.toFixed(2)
      const yearlyPrice = this.yearlyPriceValue.toFixed(2)
      this.buttonTextTarget.textContent = loading
        ? 'Processing...'
        : (isYearly ? `Subscribe - $${yearlyPrice}/year` : `Subscribe - $${monthlyPrice}/month`)
    }
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle('hidden', !loading)
    }
  }

  showError(message) {
    if (this.hasErrorsTarget) {
      this.errorsTarget.textContent = message
      this.errorsTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorsTarget) {
      this.errorsTarget.classList.add('hidden')
    }
  }
}
